import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { NavbarActionButton } from '../interfaces/navbar-action-button.interface';

/**
 * Service for managing dynamic navbar action buttons.
 *
 * This service allows components to register buttons that should appear
 * in the navbar action area. Buttons can be route-specific or global.
 *
 * @example
 * ```typescript
 * // In a component that wants to add buttons to the navbar
 * constructor(private navbarButtons: NavbarActionButtonService) {}
 *
 * ngOnInit() {
 *   this.navbarButtons.registerButton({
 *     id: 'refresh-cache',
 *     label: 'Refresh cache',
 *     icon: 'sync',
 *     useMatIcon: true,
 *     visibleOnRoutes: ['deviceSearch', 'dashboard'],
 *     onOptionSelected: (option) => this.handleRefresh()
 *   });
 * }
 *
 * ngOnDestroy() {
 *   this.navbarButtons.unregisterButton('refresh-cache');
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class NavbarActionButtonService {
  private buttons$ = new BehaviorSubject<NavbarActionButton[]>([]);

  /**
   * Register a button to appear in the navbar
   * @param button Button configuration
   */
  public registerButton(button: NavbarActionButton): void {
    const currentButtons = this.buttons$.value;

    // Check if button already exists
    const existingIndex = currentButtons.findIndex(b => b.id === button.id);

    if (existingIndex >= 0) {
      // Update existing button
      const updatedButtons = [...currentButtons];
      updatedButtons[existingIndex] = button;
      this.buttons$.next(updatedButtons);
    } else {
      // Add new button
      this.buttons$.next([...currentButtons, button]);
    }
  }

  /**
   * Unregister a button from the navbar
   * @param buttonId Unique button identifier
   */
  public unregisterButton(buttonId: string): void {
    const currentButtons = this.buttons$.value;
    const filteredButtons = currentButtons.filter(b => b.id !== buttonId);
    this.buttons$.next(filteredButtons);
  }

  /**
   * Get buttons for a specific route
   * @param route Current route name
   * @returns Observable of buttons visible on this route
   */
  public getButtonsForRoute(route: string | null): Observable<NavbarActionButton[]> {
    return this.buttons$.pipe(
      map(buttons => {
        if (!route) {
          return [];
        }

        // Filter buttons visible on this route
        const visibleButtons = buttons.filter(button => {
          // If no routes specified, button is global
          if (!button.visibleOnRoutes || button.visibleOnRoutes.length === 0) {
            return true;
          }
          // Check if current route contains any of the button's visible route patterns
          return button.visibleOnRoutes.some(routePattern => route.includes(routePattern));
        });

        // Sort by order (lower numbers first), then by ID for consistency
        return visibleButtons.sort((a, b) => {
          const orderA = a.order ?? 999;
          const orderB = b.order ?? 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.id.localeCompare(b.id);
        });
      })
    );
  }

  /**
   * Clear all registered buttons
   */
  public clearAll(): void {
    this.buttons$.next([]);
  }

  /**
   * Get all registered buttons (mainly for debugging)
   */
  public getAllButtons(): Observable<NavbarActionButton[]> {
    return this.buttons$.asObservable();
  }
}
